import { logError } from '@ptek/client-logger';
import { useWidgetEvents } from '@ptek/widget-core';
import { useCallback, useEffect, useRef } from 'react';
import { sessionsClient } from '../api-clients/sessions';
import { WIDGET_EVENTS } from '../constants/events';
import { useRecordingStore } from '../stores';
import { Appointment } from '../types';
import type { StartSetup } from '../utils/buildStartSetup';

/**
 * Platform-side session id resolution (unified-session-id mode). Lazy minting:
 * no session is created at mount/startup. The reconciler effect only adopts a
 * host-supplied id (reopen with saved session); minting is deferred to an
 * explicit commitment point (record-start or discard-and-start-new).
 *
 * Callers that need a minted id at a commitment point call `mintSession`
 * directly. Mint failures throw — callers must surface them visibly.
 */
export const useSessionIdResolution = () => {
  const { emitToHost } = useWidgetEvents();
  const mintInFlightRef = useRef(false);

  const unifiedSessionIdEnabled = useRecordingStore(
    (state) => state.unifiedSessionIdEnabled
  );
  const recordingSessionId = useRecordingStore(
    (state) => state.recordingSessionId
  );
  const activeRecordingSession = useRecordingStore(
    (state) => state.activeRecordingSession
  );

  const associateSessionWithEncounter = useCallback(
    async (sessionId: string, appointment: Appointment): Promise<void> => {
      emitToHost(WIDGET_EVENTS.EMITTER_NAMES.SAVE_SESSION_ID, {
        sessionId,
        encounterId: appointment.id,
      });
      try {
        await sessionsClient.updateSession(sessionId, {
          encounterId: appointment.id,
          patientName: appointment.patientName,
        });
      } catch (error) {
        logError(
          'useSessionIdResolution',
          'Failed to associate session with encounter',
          error
        );
      }
    },
    [emitToHost]
  );

  /**
   * Mint a new session at a commitment point (record-start, discard flow).
   * Throws on failure — callers must surface the error visibly.
   *
   * @param opts.forceNew When true, always create a fresh session: skip the
   *   adopt-guard and do not touch the store or emit SAVE_SESSION_ID. The
   *   caller owns store-set + host emit once its own teardown succeeds (used by
   *   the discard/start-fresh path so a mint failure is a no-op).
   */
  const mintSession = useCallback(
    async (
      appointment: Appointment | null,
      opts?: { forceNew?: boolean; startSetup?: StartSetup }
    ): Promise<string> => {
      if (mintInFlightRef.current) {
        throw new Error('Session mint already in flight');
      }
      mintInFlightRef.current = true;
      try {
        const created = await sessionsClient.createSession({
          patientName: appointment?.patientName || 'Unknown Patient',
          encounterId: appointment?.id,
          visitType: appointment?.title || 'Recording Session',
          visitDate: new Date().toISOString(),
          duration: 0,
          provider: 'Current Provider',
          transcript: [],
          ...(opts?.startSetup && { startSetup: opts.startSetup }),
        });

        if (opts?.forceNew) {
          return created.id;
        }

        const store = useRecordingStore.getState();
        if (store.recordingSessionId) {
          // A host-supplied id was adopted meanwhile — never clobber it.
          return store.recordingSessionId;
        }

        store.setRecordingSessionId(created.id);

        emitToHost(WIDGET_EVENTS.EMITTER_NAMES.SAVE_SESSION_ID, {
          sessionId: created.id,
          encounterId: appointment?.id,
        });
        return created.id;
      } finally {
        mintInFlightRef.current = false;
      }
    },
    [emitToHost]
  );

  /** Adopt host-supplied sessionId on SET_ENCOUNTER (reopen). No minting. */
  useEffect(() => {
    if (!unifiedSessionIdEnabled || recordingSessionId) return;

    if (activeRecordingSession?.sessionId) {
      useRecordingStore
        .getState()
        .setRecordingSessionId(activeRecordingSession.sessionId);
    }
  }, [unifiedSessionIdEnabled, recordingSessionId, activeRecordingSession]);

  return { associateSessionWithEncounter, mintSession };
};
